import { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import { UploadTenderDocumentModal } from "components/bids/bid_documents/UploadTenderDocumentModal";
import { useDocumentUploadProgress } from "components/content_library/DocumentUploadProgressProvider";
import { noticeSearchTarget, noticeTargetWithView } from "components/notices/utils";
import { noticesV2TargetWithFilters } from "components/notices_v2/noticesV2TargetWithFilters";
import {
  ANALYSE_THE_MARKET_RELEVANCY_COLUMN_FIELDS,
  FIND_EXPIRING_CONTRACTS_RELEVANCY_COLUMN_FIELDS,
  FIND_OPEN_TENDERS_RELEVANCY_COLUMN_FIELDS,
} from "components/notices_v2/NoticeTableV2";
import { CreditAccessWrapper } from "components/paywall/CreditAccessWrapper";
import { generateSavedViewTarget } from "components/saved_searches/utils";
import Card from "components/ui/card/Card";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import toast from "components/ui/toast/Toast";
import EllipsisText from "components/ui/tooltip/EllipsisText";
import ViewAllButton from "components/views/ViewAllButton";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { RelevanceScoreEnum } from "lib/generated/saved-views/viewConfiguration";
import { useRemainingCredits } from "lib/hooks/api/admin/credits/useRemainingCredits";
import { useBidWorkflow } from "lib/hooks/api/bid/bids/useBidWorkflow";
import { useCreateBid } from "lib/hooks/api/bid/bids/useCreateBid";
import { useTriggerBidDocumentUpload } from "lib/hooks/api/customer_documents/useTriggerBidDocumentUpload";
import { useSavedViews } from "lib/hooks/api/saved_views/useSavedViews";
import { useTeam } from "lib/hooks/api/teams/useTeam";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useSubscription } from "lib/hooks/subscription";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { useBidOrchestratorStreamContext } from "lib/providers/bid_orchestrator/BidOrchestratorStreamProvider";
import { usePaywall } from "lib/providers/Paywall";
import { routes, routeSegments } from "lib/routes";
import { toAndSearch } from "lib/spaNavigation";
import { EventNames, useTracking } from "lib/tracking";
import {
  BidReportType,
  NoticesStagesv2,
  SavedViewDataType,
  SavedViewSortBy,
  SavedViewSortOrder,
} from "lib/types/graphQLEnums";
import PartCard from "./PartCard";
import { DEFAULT_VIEW_IDS } from "./util";

function Welcome() {
  const subscription = useSubscription();
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();
  const isRelevancyNewUsers = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { data: team, isLoading: isTeamLoading } = useTeam({ enabled: isRelevancyNewUsers });
  const relevancyTopicLinksReady = !isRelevancyNewUsers || !isTeamLoading;

  const hasBidsAccess = subscription.hasDataTypes("BIDS");
  const hasDocumentsAccess = subscription.hasDataTypes("DOCUMENTS");

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const dataTypes = [
    SavedViewDataType.NoticeSemantic,
    ...(hasDocumentsAccess ? [SavedViewDataType.Document] : []),
  ];

  const { data: savedViewsRecent, isLoading } = useSavedViews(
    {
      dataTypes,
      orderBy: SavedViewSortBy.UpdatedAt,
      order: SavedViewSortOrder.Desc,
    },
    { page: 1, pageSize: 4 },
    { enabled: isRelevancyNewUsers },
  );
  const navigate = useNavigate();

  // The bidId we created for this orchestrator cycle. Drives both the
  // post-upload dispatch effect (so we only act on our upload) and the
  // auto-nav watcher (gates against unrelated enrichments on other bids).
  const [pendingBidId, setPendingBidId] = useState<string | undefined>(undefined);

  // Scoped to the bid we're creating — reads are already isolated to this
  // operation, so no manual operationId guard is needed below.
  const { allUploaded, uploadStatuses } = useDocumentUploadProgress(pendingBidId);
  // skipProcessing defers document processing to the bid orchestrator workflow
  // so we can dispatch (and navigate) as soon as S3 uploads settle, instead of
  // waiting on the backend processing pipeline to finish first. The workflow
  // picks up UPLOADING docs with a storagePath and processes them itself.
  const triggerUpload = useTriggerBidDocumentUpload(undefined, { skipProcessing: true });

  const { data: workflow } = useBidWorkflow();
  const defaultStageId = workflow?.transitions[0]?.stage.id;

  const { mutate: createBid } = useCreateBid({
    "Page source": "Homepage",
    "Context source": "Requirement summary creation",
  });

  const { queueBidOrchestrator } = useBidOrchestratorStreamContext();
  const prevAllUploadedRef = useRef(allUploaded);

  // On the S3-upload settle edge (`allUploaded` flipping true), queue the bid
  // orchestrator and hand off to the loading page. The single BidOrchestratorDriver
  // (in BidOrchestratorStreamProvider) sees the queued orchestrator and fires
  // `startBidOrchestrator` — Welcome no longer dispatches itself. The loading page
  // forwards to `/requirement-summary-report/$reportId` once `*_started` arrives and
  // bails on failure; the queued orchestrator is its mount guard until the entry
  // propagates, after which the driver clears it.
  //
  // Backend document processing is deferred to the orchestrator workflow itself
  // (`skipProcessing: true`), so we only wait for S3 upload to finish here.
  useEffect(() => {
    const wasNotAllUploaded = !prevAllUploadedRef.current;
    prevAllUploadedRef.current = allUploaded;

    const justFinishedUploading = wasNotAllUploaded && allUploaded;
    if (!justFinishedUploading || !pendingBidId) return;
    // Vacuous `allUploaded` true (no files yet) — wait for the real upload.
    if (uploadStatuses.length === 0) return;

    const bidId = pendingBidId;
    // Welcome creates a new bid from scratch and immediately wants the
    // requirement summary report — extract bid details so the empty bid gets a title,
    // and skip the requirement summary (the user didn't ask for it).
    queueBidOrchestrator(bidId, {
      reportType: BidReportType.RequirementSummary,
      expectsUpload: false,
      extractBidDetails: true,
      // Brand-new bid — no pre-existing docs; the uploaded ones flow in via upload state.
      existingDocumentIds: [],
    });
    setPendingBidId(undefined);
    void navigate({ to: `/bids/${bidId}/${routeSegments.requirementSummary}` });
  }, [allUploaded, pendingBidId, uploadStatuses, queueBidOrchestrator, navigate]);

  const { openPaywallModal } = usePaywall();

  const { data: credits } = useRemainingCredits(
    { creditType: "BID" },
    {
      enabled: !hasBidsAccess,
    },
  );

  // TODO: hook up to consumption model using useCheckCreditsAccess - like bids above
  const hasMarketAnalysisAccess = subscription.hasDataTypes("MARKET_ANALYSIS");
  if (!currentUser) {
    return null;
  }

  const firstName = currentUser.firstName;
  const topicFromProfile = team?.metadata?.defaultSearchTopic?.trim() ?? "";

  // Called when the user clicks "Create report" inside the modal. Creates an
  // empty bid up-front (the bidDetailsExtractor inside the BidOrchestrator
  // workflow fills in the title asynchronously), then triggers the upload via
  // DocumentUploadProgressProvider. The post-upload effect above takes over
  // once filesProcessing transitions back to false.
  const handleModalSubmit = (files: File[]) => {
    if (!defaultStageId) {
      toast.error("Could not determine default stage for bid");
      return;
    }
    createBid(
      { bid: { name: "", stageId: defaultStageId }, entities: { entities: [] } },
      {
        onSuccess: (data) => {
          const newBidId = data.createBid.id;
          setPendingBidId(newBidId);
          triggerUpload(files, newBidId);
          toast.message(
            "We're just analysing your tender documents to extract the bid requirements. We'll take you to the report when it's ready.",
          );
        },
        onError: () => {
          toast.error("Failed to create bid");
        },
      },
    );
  };

  const strategyTarget = isRelevancyNewUsers
    ? noticesV2TargetWithFilters(
        {
          topicSearch: topicFromProfile,
          publishDate: {
            relativeFrom: "P-3Y",
          },
          stage: ["AWARDED_CONTRACT", "EXPIRED_CONTRACT"],
        },
        ANALYSE_THE_MARKET_RELEVANCY_COLUMN_FIELDS,
      )
    : noticeSearchTarget({
        publishDate: {
          relativeFrom: "P-3Y",
        },
        stage: [NoticesStagesv2.AwardedContract, NoticesStagesv2.ExpiredContract],
        relevanceScore: [RelevanceScoreEnum.Low],
      });

  const pipelineTarget = isRelevancyNewUsers
    ? noticesV2TargetWithFilters(
        {
          topicSearch: topicFromProfile,
          stage: ["AWARDED_CONTRACT"],
          expiryDate: {
            relativeFrom: "PT0S",
            hideNulls: true,
          },
        },
        FIND_EXPIRING_CONTRACTS_RELEVANCY_COLUMN_FIELDS,
      )
    : noticeTargetWithView(DEFAULT_VIEW_IDS.upcomingContractExpiries);

  const tendersTarget = isRelevancyNewUsers
    ? noticesV2TargetWithFilters(
        {
          topicSearch: topicFromProfile,
          stage: ["OPEN_TENDER"],
        },
        FIND_OPEN_TENDERS_RELEVANCY_COLUMN_FIELDS,
      )
    : noticeTargetWithView(DEFAULT_VIEW_IDS.openOpportunities);

  const bidsPartCard = (
    <CreditAccessWrapper requiredDataType="BIDS" creditType="BID" paywallFeatureType="BIDS">
      <PartCard
        partType="Bids"
        onClick={() => {
          // Have to include this otherwise a quick click will get them through paywall
          if (hasBidsAccess || (credits && credits?.remainingCredits > 0)) {
            logEvent(EventNames.qualificationStarted, { "Context source": "Homepage" });
            setIsUploadModalOpen(true);
          }
        }}
      />
    </CreditAccessWrapper>
  );

  const pipelineCard = (
    <PartCard
      partType="Pipeline"
      {...(relevancyTopicLinksReady ? pipelineTarget : {})}
      onClick={() => {
        if (!relevancyTopicLinksReady) {
          return;
        }
        logEvent(EventNames.buildPipelineStarted);
      }}
    />
  );

  const tendersCard = (
    <PartCard
      partType="Tenders"
      {...(relevancyTopicLinksReady ? tendersTarget : {})}
      onClick={() => {
        if (!relevancyTopicLinksReady) {
          return;
        }
        logEvent(EventNames.trackTendersStarted);
      }}
    />
  );

  const strategyCard = (
    <PartCard
      partType="Strategy"
      hasPaywall={!hasMarketAnalysisAccess}
      {...strategyTarget}
      onClick={(e) => {
        if (!hasMarketAnalysisAccess) {
          e.preventDefault();
          openPaywallModal("STRATEGY", {
            requiredDataSubscription: "MARKET_ANALYSIS",
            strategyPaywall: true,
          });
          return;
        }
        logEvent(EventNames.createStrategyStarted);
      }}
    />
  );

  const documentsCard = (
    <PartCard
      partType="Documents"
      hasPaywall={!hasDocumentsAccess}
      {...toAndSearch(routes.documentsEmptyState)}
      onClick={(e) => {
        logEvent(EventNames.findBuyerDocumentsStarted);
        if (!hasDocumentsAccess) {
          e.preventDefault();
          openPaywallModal("DOCUMENTS");
        }
      }}
    />
  );

  function renderSearchActivityContent() {
    if (isLoading) {
      return (
        <RecentSavedSearchesContainer>
          {[...Array(4)].map((_, index) => (
            <SkeletonText
              key={index}
              width="100%"
              fontSize={82}
              variant="rectangular"
              animationDelayMs={index * 120}
              customSpacing={true}
            />
          ))}
        </RecentSavedSearchesContainer>
      );
    }
    if (savedViewsRecent.results.length === 0) {
      return <Text color="sysTextPlaceholder">Your saved searches will show here</Text>;
    }
    return (
      <RecentSavedSearchesContainer>
        {savedViewsRecent.results.map((view) => (
          <Card key={view.id} {...generateSavedViewTarget(view.id, view.dataType)}>
            <Flex alignItems="center" gap={8}>
              <SearchCardIcon alignItems="center">
                <UIcon
                  icon={view.dataType === "document" ? "box" : "file02"}
                  size={16}
                  color="sysTextDefault"
                />
              </SearchCardIcon>
              <Flex flexGrow={1} minWidth={0}>
                <EllipsisText fontWeight="500" ellipsisMethod="lineClamp" ellipsisLines={1}>
                  {view.name}
                </EllipsisText>
              </Flex>
            </Flex>
          </Card>
        ))}
      </RecentSavedSearchesContainer>
    );
  }

  return (
    <Flex flexDirection="column" gap="24px">
      <TitleText>
        Welcome to Stotles, <TitleTextName as="span">{firstName}</TitleTextName>
      </TitleText>
      <WelcomeContentWrapper flexDirection="column" gap={16}>
        {isRelevancyNewUsers && (
          <Wrapper>
            <Flex justifyContent="space-between" alignItems="center">
              <Text bold>Saved searches</Text>
              <div>
                <ViewAllButton title="All saved searches" filters={{ dataTypes }} />
              </div>
            </Flex>
            {renderSearchActivityContent()}
          </Wrapper>
        )}
        <Text bold>Where would you like to get started?</Text>
        <CardsContainer>
          {isRelevancyNewUsers ? (
            <>
              {tendersCard}
              {bidsPartCard}
              {pipelineCard}
              {documentsCard}
            </>
          ) : (
            <>
              {bidsPartCard}
              {strategyCard}
              {pipelineCard}
              {tendersCard}
            </>
          )}
        </CardsContainer>
      </WelcomeContentWrapper>

      <UploadTenderDocumentModal
        bidId={pendingBidId}
        isOpen={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        sourceUrl={undefined}
        primaryButtonText="Create report"
        onSubmit={handleModalSubmit}
      />
    </Flex>
  );
}

const WelcomeContentWrapper = styled(Flex)({
  paddingTop: "24px",
});

const TitleText = styled.h1(({ theme }) => ({
  color: theme.colors.sysTextDefault,
  fontSize: "29px",
  fontWeight: "500",
  lineHeight: "125%",
  margin: 0,
}));

const TitleTextName = styled(TitleText)({
  fontWeight: "700",
});

const Wrapper = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  padding: "0 0 24px",
});

const SearchCardIcon = styled(Flex)({
  flexShrink: 0,
});

const RecentSavedSearchesContainer = styled.div({
  display: "grid",
  gap: "16px",
  gridTemplateColumns: "repeat(4, 1fr)",
  "@media (max-width: 960px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
});

const CardsContainer = styled.div({
  display: "grid",
  gap: "16px",
  gridTemplateColumns: "repeat(4, 1fr)",
  "@media (max-width: 960px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
});

export default Welcome;
